import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DomainCompleteness } from '@/lib/domainSchema'

export type { DomainCompleteness }

interface DomainCardProps {
  domainKey: string
  label: string
  completeness: DomainCompleteness
  /** One-paragraph human summary, composed from the domain's sub-fields. */
  summary: string | null
  to: string
}

const STATUS_LABEL: Record<DomainCompleteness, string> = {
  complete: 'Complete',
  partial: 'Partial',
  thin: 'Light',
  empty: 'No context yet',
}

const STATUS_DOT: Record<DomainCompleteness, string> = {
  complete: 'bg-success',
  partial: 'bg-warning',
  thin: 'bg-info',
  empty: 'bg-text-tertiary',
}

/**
 * Single tile for the domain grid. Card-as-link so the whole surface is
 * clickable; the trailing arrow icon is the visual affordance for
 * "drill in." Empty state collapses the summary into a one-liner pointing
 * the user back at upload.
 */
export function DomainCard({
  domainKey,
  label,
  completeness,
  summary,
  to,
}: DomainCardProps) {
  const isEmpty = completeness === 'empty' || !summary
  return (
    <Link
      to={to}
      data-testid={`domain-card-${domainKey}`}
      className={cn(
        'group block rounded-lg border border-border-default bg-bg-primary p-4',
        'hover:border-border-hover hover:bg-bg-elevated transition-colors duration-150'
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-display font-semibold text-text-primary">
          {label}
        </span>
        <ArrowUpRight
          size={14}
          className="text-text-tertiary group-hover:text-text-primary transition-colors"
        />
      </div>
      <p className="text-sm text-text-secondary leading-relaxed line-clamp-3 min-h-[3.75rem]">
        {isEmpty
          ? 'No context yet — upload a doc to start building this section.'
          : summary}
      </p>
      <div className="mt-3 flex items-center gap-2 text-xs text-text-tertiary">
        <span
          aria-hidden
          className={cn('w-1.5 h-1.5 rounded-full', STATUS_DOT[completeness])}
        />
        {STATUS_LABEL[completeness]}
      </div>
    </Link>
  )
}
